import { useMemo } from "react";

import { Grid } from "@mui/material";
import { Emptyable, formatBodyForRecipient } from "@simplyk/common";

import { useCurrentUserContext } from "../../../../contexts/CurrentUserContext";
import { useLocaleContext } from "../../../../contexts/LocaleContext";
import { Locales } from "../../../../gql/gql-types";
import { trpc } from "../../../../helpers/trpc";
import {
  emailFooterEn,
  emailFooterFr,
} from "../../../FullScreenEmailEditorContainer/FullScreenEmailEditorPreview/footerHtmlTemplates";

import { DashboardIcon } from "@/dashboard-design-system/Icon/DashboardIcon";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { User } from "@/icons/outlined";

interface EmailPreviewProps {
  body: string;
  emailContentId?: Emptyable<string>;
  recipientEmails: string[];
  subject: string;
  senderName: Emptyable<string>;
  htmlOnly?: boolean;
}

const defaultRecipient = (locale: Locales) => ({
  firstName: "John",
  lastName: "Smith",
  email: "john.smith@gmail.com",
  id: "",
  hasUnsubscribed: false,
  contactId: "",
  locale,
});

export const EmailPreview = ({ body, emailContentId, recipientEmails, subject, senderName }: EmailPreviewProps) => {
  const { locale } = useLocaleContext();
  const { organization, currentUser } = useCurrentUserContext();
  const { isSmallScreen } = useMediaQuery();
  const { data: emailDeliveryVariables, refetch } = trpc.getPreviewEmailDeliveryVariables.useQuery(
    {
      emailContentId: emailContentId as string,
      recipientEmails,
      occurrenceId: undefined,
    },
    { enabled: Boolean(emailContentId) }
  );

  const formattedBody = useMemo(() => {
    if (emailContentId) {
      refetch();
    }
    return formatBodyForRecipient({
      frontendUrl: process.env.NEXT_PUBLIC_FRONTEND_URL as string,
      recipient: emailDeliveryVariables?.recipients?.[0] || defaultRecipient(locale),
      body,
      variables: emailDeliveryVariables?.variables,
      locale,
    });
  }, [body, emailContentId, emailDeliveryVariables?.recipients, emailDeliveryVariables?.variables, locale, refetch]);

  const formattedBodyWithStyles = `
  <style>
    a {
      color: #0000EE;
      text-decoration: underline;
    }
  </style>
  ${formattedBody}
`;

  return (
    <Grid
      container
      height="100%"
      direction="column"
      flexWrap="nowrap"
      sx={{ overflowY: "auto" }}
      paddingRight={isSmallScreen ? 0 : 2}
    >
      <Grid item>
        <Grid container padding={2} paddingTop={0}>
          {subject && (
            <Grid xs={12} item marginBottom={1}>
              <DashboardTypography variant="subtitle1" vibe="text-neutral-moderate">
                {subject}
              </DashboardTypography>
            </Grid>
          )}
          <Grid item xs={12}>
            <Grid container alignItems="center">
              <Grid
                item
                marginRight={1.5}
                alignItems="center"
                justifyContent="center"
                display="flex"
                sx={(theme) => ({
                  height: "28px",
                  width: "28px",
                  borderRadius: "50%",
                  background: theme.palette.surface.neutral.quiet,
                })}
              >
                <DashboardIcon size="small" vibe="text-neutral-supershy">
                  <User />
                </DashboardIcon>
              </Grid>
              <Grid item xs>
                <DashboardTypography variant="subtitle2" vibe="text-neutral-moderate">
                  {senderName || organization?.name}
                </DashboardTypography>
                <DashboardTypography variant="caption" vibe="text-neutral-moderate">
                  {currentUser?.email}
                </DashboardTypography>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs>
        <Grid
          container
          sx={(theme) => ({
            backgroundColor: theme.palette.surface.neutral.quiet,
            paddingTop: isSmallScreen ? 2 : 5,
            paddingBottom: isSmallScreen ? 2 : 5,
            paddingRight: isSmallScreen ? 1 : 0,
            paddingLeft: isSmallScreen ? 1 : 0,
            height: "100%",
            overflowY: "auto",
            display: "flex",
            justifyContent: "center",
            borderRadius: theme.radius(1),
          })}
        >
          <Grid item sx={{ maxWidth: 427 }}>
            <Grid container borderRadius={1} sx={{ backgroundColor: "white" }}>
              <Grid item paddingLeft={3} paddingRight={3} paddingTop={2} paddingBottom={2}>
                <div dangerouslySetInnerHTML={{ __html: formattedBodyWithStyles }} />
              </Grid>
              <Grid
                item
                container
                sx={(theme) => ({
                  backgroundColor: "#F9F9F9",
                  padding: theme.spacing(2, 3),
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "center",
                  alignItems: "center",
                  borderBottomLeftRadius: theme.radius(1),
                  borderBottomRightRadius: theme.radius(1),
                })}
              >
                <div dangerouslySetInnerHTML={{ __html: locale === Locales.FR ? emailFooterFr : emailFooterEn }} />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};
